import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, RefreshCw } from 'lucide-react';

interface SectionShellProps {
    title: ReactNode;
    icon?: ReactNode;
    /** Optional "View all" link. */
    linkTo?: string;
    linkLabel?: string;
    loading?: boolean;
    error?: string | null;
    onRetry?: () => void;
    /** Number of skeleton rows while loading. */
    skeletonRows?: number;
    children: ReactNode;
}

/** Common header + loading/error handling for every dashboard block. */
export function SectionShell({ title, icon, linkTo, linkLabel = 'View all', loading, error, onRetry, skeletonRows = 2, children }: SectionShellProps) {
    return (
        <section className="dashboard-section">
            <div className="section-header">
                <h2>{icon} {title}</h2>
                {linkTo && (
                    <Link to={linkTo} className="section-link">
                        {linkLabel} <ChevronRight size={16} aria-hidden="true" />
                    </Link>
                )}
            </div>
            {loading ? (
                <div className="dash-skeleton-list" aria-busy="true" aria-label="Loading">
                    {Array.from({ length: skeletonRows }).map((_, i) => <div key={i} className="skeleton dash-skeleton-row" />)}
                </div>
            ) : error ? (
                <div className="inline-alert inline-alert-warning dash-section-error" role="alert">
                    <span>Couldn't load this right now.</span>
                    {onRetry && (
                        <button type="button" className="link-btn dash-retry" onClick={onRetry}>
                            <RefreshCw size={14} aria-hidden="true" /> Retry
                        </button>
                    )}
                </div>
            ) : children}
        </section>
    );
}
